import { getScrollContainer, scrollContainerMediaQuery } from '@theme/scroll-container';

const catalogueParentSelector = '[data-jesoli-catalogue-parent]';
const catalogueParentItemSelector = '[data-jesoli-catalogue-parent-item]';
const catalogueChildSelector = '[data-jesoli-catalogue-child][href]';
const homeAnchorSelector = '[data-jesoli-home-anchor][href]';
const desktopMediaQuery = '(min-width: 750px)';
const headerControllers = new WeakMap();

class JesoliHeaderCatalogueController {
  #abortController;
  #closeTimer;
  #desktopMedia;
  #header;
  #initialized = false;
  #parentButton;
  #parentItem;
  #submenu;

  constructor(header) {
    this.#header = header;
  }

  initialize() {
    if (this.#initialized) return;

    this.#initialized = true;
    this.#desktopMedia = window.matchMedia(desktopMediaQuery);
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    for (const link of this.#header.querySelectorAll(homeAnchorSelector)) {
      link.addEventListener('click', this.#handleHomeAnchorClick, { signal });
    }

    const parentButton = this.#header.querySelector(catalogueParentSelector);
    const parentItem = parentButton?.closest(catalogueParentItemSelector);
    const submenu = parentItem?.querySelector('[data-jesoli-catalogue-submenu]');
    if (!(parentButton instanceof HTMLButtonElement) || !(parentItem instanceof HTMLElement) || !(submenu instanceof HTMLElement)) {
      this.#activateHashTargetFromLocation();
      return;
    }

    this.#parentButton = parentButton;
    this.#parentItem = parentItem;
    this.#submenu = submenu;

    parentButton.addEventListener('click', this.#handleParentClick, { signal });
    parentButton.addEventListener('keydown', this.#handleParentKeydown, { signal });
    parentItem.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
    parentItem.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    parentItem.addEventListener('focusin', this.#handleFocusIn, { signal });
    parentItem.addEventListener('focusout', this.#handleFocusOut, { signal });
    parentItem.addEventListener('keydown', this.#handleMenuKeydown, { signal });
    document.addEventListener('pointerdown', this.#handleOutsidePointerDown, { signal });
    this.#desktopMedia.addEventListener('change', this.#handleBreakpointChange, { signal });

    const parentHandle = parentButton.dataset.jesoliCatalogueParentHandle || '';
    const label = parentButton.dataset.jesoliCatalogueLabel || '';
    const homeUrl = parentButton.dataset.jesoliCatalogueHomeUrl || '/';
    const childLinks = new Set(parentItem.querySelectorAll(catalogueChildSelector));

    const drawerTrigger = this.#header.querySelector(`#HeaderDrawer-${CSS.escape(parentHandle)}`);
    const drawerItem = drawerTrigger?.closest('li');
    if (drawerItem) {
      const drawerLabel = drawerTrigger.querySelector('.menu-drawer__menu-item-text');
      if (label && drawerLabel) drawerLabel.textContent = label;
      if (parentButton.classList.contains('menu-list__link--active')) {
        drawerTrigger.classList.add('menu-drawer__menu-item--active');
      }
      for (const link of drawerItem.querySelectorAll('a[href]')) childLinks.add(link);
    }

    for (const link of childLinks) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      if (link.hasAttribute('data-jesoli-catalogue-direct')) continue;

      const key = this.#collectionKeyFromLink(link);
      if (!key) continue;

      link.dataset.jesoliCatalogueKey = key;
      link.dataset.jesoliCatalogueOriginalHref = link.href;
      link.href = this.#homepageCatalogueUrl(homeUrl, key);
      link.addEventListener('click', this.#handleCatalogueClick, { signal });
    }

    this.#activateHashTargetFromLocation();
  }

  destroy() {
    if (!this.#initialized) return;

    window.clearTimeout(this.#closeTimer);
    this.#closeMenu();
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#closeTimer = undefined;
    this.#desktopMedia = undefined;
    this.#parentButton = undefined;
    this.#parentItem = undefined;
    this.#submenu = undefined;
    this.#initialized = false;
  }

  #collectionKeyFromLink(link) {
    const existingKey = link.dataset.jesoliCatalogueKey;
    if (existingKey) return existingKey;

    const originalHref = link.dataset.jesoliCatalogueOriginalHref;
    const url = new URL(originalHref || link.getAttribute('href') || '', window.location.origin);
    const queryKey = url.searchParams.get('catalogue');
    if (queryKey) return queryKey;

    const match = url.pathname.match(/\/collections\/([^/]+)\/?$/);
    if (!match) return '';

    const handle = decodeURIComponent(match[1]);
    return handle === 'all' ? 'shop-all' : handle;
  }

  #homepageCatalogueUrl(homeUrl, key) {
    const url = new URL(homeUrl, window.location.origin);
    url.searchParams.set('catalogue', key);
    url.hash = 'shop';
    return url.toString();
  }

  #isDesktop() {
    return this.#desktopMedia?.matches === true;
  }

  #isOpen() {
    return this.#parentItem?.hasAttribute('data-jesoli-catalogue-open') === true;
  }

  #openMenu({ focusFirst = false } = {}) {
    if (!this.#isDesktop() || !this.#parentItem || !this.#parentButton) return;

    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
    this.#parentItem.setAttribute('data-jesoli-catalogue-open', '');
    this.#parentButton.setAttribute('aria-expanded', 'true');

    if (focusFirst) {
      const firstLink = this.#submenu?.querySelector(catalogueChildSelector);
      if (firstLink instanceof HTMLAnchorElement) firstLink.focus();
    }
  }

  #closeMenu({ restoreFocus = false } = {}) {
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
    this.#parentItem?.removeAttribute('data-jesoli-catalogue-open');
    this.#parentButton?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.#parentButton?.focus();
  }

  #scheduleClose() {
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => {
      const activeElement = document.activeElement;
      if (this.#parentItem?.matches(':hover')) return;
      if (activeElement instanceof Node && this.#parentItem?.contains(activeElement)) return;
      this.#closeMenu();
    }, 120);
  }

  #handleParentClick = (event) => {
    if (!this.#isDesktop()) return;

    event.preventDefault();
    if (this.#isOpen()) this.#closeMenu();
    else this.#openMenu();
  };

  #handleParentKeydown = (event) => {
    if (!this.#isDesktop()) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#openMenu({ focusFirst: true });
    }
  };

  #handleMenuKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#isOpen()) return;

    event.preventDefault();
    event.stopPropagation();
    this.#closeMenu({ restoreFocus: true });
  };

  #handlePointerEnter = () => {
    this.#openMenu();
  };

  #handlePointerLeave = () => {
    this.#scheduleClose();
  };

  #handleFocusIn = (event) => {
    if (event.target === this.#parentButton && !this.#parentButton.matches(':focus-visible')) return;
    this.#openMenu();
  };

  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.#parentItem?.contains(event.relatedTarget)) return;
    this.#scheduleClose();
  };

  #handleOutsidePointerDown = (event) => {
    if (!(event.target instanceof Node) || this.#parentItem?.contains(event.target)) return;
    this.#closeMenu();
  };

  #handleBreakpointChange = () => {
    this.#closeMenu();
  };

  #handleCatalogueClick = (event) => {
    const link = event.currentTarget;
    if (!(link instanceof HTMLAnchorElement)) return;

    const catalogue = document.querySelector('jesoli-featured-catalogue[data-section-id]');
    if (!catalogue || typeof catalogue.selectCategory !== 'function') return;

    const key = link.dataset.jesoliCatalogueKey || '';
    if (!key) return;
    if (typeof catalogue.hasCategory === 'function' && !catalogue.hasCategory(key)) {
      link.href = link.dataset.jesoliCatalogueOriginalHref || link.href;
      return;
    }

    event.preventDefault();
    this.#closeMenu();

    const drawer = link.closest('header-drawer');
    const closeButton = drawer?.querySelector('.menu-drawer__close-button');
    if (closeButton instanceof HTMLButtonElement) {
      closeButton.click();
      this.#runAfterDrawerCloses(drawer, () => catalogue.selectCategory(key, { focus: true, scroll: true }));
    } else {
      catalogue.selectCategory(key, { focus: true, scroll: true });
    }
  };

  #handleHomeAnchorClick = (event) => {
    const link = event.currentTarget;
    if (!(link instanceof HTMLAnchorElement)) return;

    const anchor = link.dataset.jesoliHomeAnchor || '';
    const target = document.getElementById(anchor);
    if (!(target instanceof HTMLElement)) return;

    event.preventDefault();
    this.#closeMenu();

    const activate = () => this.#scrollToHomeTarget(target, { updateHistory: true, focus: true });
    const drawer = link.closest('header-drawer');
    const closeButton = drawer?.querySelector('.menu-drawer__close-button');
    if (closeButton instanceof HTMLButtonElement) {
      closeButton.click();
      this.#runAfterDrawerCloses(drawer, activate);
    } else {
      activate();
    }
  };

  #runAfterDrawerCloses(drawer, callback) {
    const details = drawer?.querySelector('#Details-menu-drawer-container');
    if (!(details instanceof HTMLDetailsElement) || !details.open) {
      window.requestAnimationFrame(callback);
      return;
    }

    const startedAt = performance.now();
    const waitForClose = () => {
      const scrollUnlocked = !document.documentElement.hasAttribute('scroll-lock');
      if (!details.open && scrollUnlocked) {
        window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
        return;
      }

      // The drawer normally finishes in a few hundred milliseconds. Stop
      // cleanly if its owner never releases the lock instead of scrolling the
      // page underneath a trapped menu.
      if (performance.now() - startedAt < 1200) window.requestAnimationFrame(waitForClose);
    };

    window.requestAnimationFrame(waitForClose);
  }

  #activateHashTargetFromLocation() {
    const anchor = window.location.hash.slice(1);
    if (anchor !== 'about' && anchor !== 'contact') return;

    window.requestAnimationFrame(() => {
      const target = document.getElementById(anchor);
      if (target instanceof HTMLElement) this.#scrollToHomeTarget(target, { updateHistory: false, focus: true, immediate: true });
    });
  }

  #scrollToHomeTarget(target, { updateHistory, focus, immediate = false }) {
    if (updateHistory) {
      const url = new URL(window.location.href);
      url.hash = target.id;
      window.history.pushState(null, '', url);
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scrollContainer = getScrollContainer();

    // Desktop uses `.page-wrapper` as its scroll container while the document root is fixed.
    // `Element.scrollIntoView()` can still move both ancestors in some Windows/Chrome zoom
    // combinations, leaving the fixed wrapper visually offset and creating blank space below
    // the footer. Reset any browser-created outer offset, then scroll only the active container.
    if (scrollContainerMediaQuery.matches && scrollContainer !== document.scrollingElement) {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }

    const targetRect = target.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const scrollMargin = Number.parseFloat(getComputedStyle(target).scrollMarginBlockStart) || 0;
    const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
    const targetScrollTop = Math.min(
      maxScrollTop,
      Math.max(0, scrollContainer.scrollTop + targetRect.top - containerRect.top - scrollMargin)
    );

    scrollContainer.scrollTo({
      top: targetScrollTop,
      behavior: immediate || reducedMotion ? 'auto' : 'smooth',
    });
    if (focus) target.focus({ preventScroll: true });
  }
}

const findHeaders = (target) => {
  if (!(target instanceof Element)) return [];

  const headers = [...target.querySelectorAll('#header-component')];
  if (target.matches('#header-component')) headers.unshift(target);
  return headers;
};

const initializeHeader = (header) => {
  if (headerControllers.has(header)) return;

  const controller = new JesoliHeaderCatalogueController(header);
  headerControllers.set(header, controller);
  controller.initialize();
};

const destroyHeader = (header) => {
  headerControllers.get(header)?.destroy();
  headerControllers.delete(header);
};

let hydrationRefreshFrame;
const refreshHydratedHeaders = () => {
  hydrationRefreshFrame = undefined;

  for (const header of document.querySelectorAll('#header-component')) {
    const childLinks = [...header.querySelectorAll(catalogueChildSelector)];
    const needsRefresh = childLinks.some((link) => !link.dataset.jesoliCatalogueKey);
    if (!needsRefresh) continue;

    destroyHeader(header);
    initializeHeader(header);
  }
};

const headerHydrationObserver = new MutationObserver(() => {
  if (hydrationRefreshFrame !== undefined) return;
  hydrationRefreshFrame = window.requestAnimationFrame(refreshHydratedHeaders);
});

for (const header of document.querySelectorAll('#header-component')) initializeHeader(header);
headerHydrationObserver.observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', (event) => {
  for (const header of findHeaders(event.target)) initializeHeader(header);
});

document.addEventListener('shopify:section:unload', (event) => {
  for (const header of findHeaders(event.target)) destroyHeader(header);
});
